import { Logo } from "@/components/branding/Logo";
import { CommercialContactCard } from "@/components/pricing/CommercialContactCard";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import {
  MessageCircle,
  Users,
  BarChart,
  Zap,
  CheckCircle2,
  ChevronRight,
  ArrowRight,
  Download,
  Calendar,
  PhoneCall,
  Mail,
  Star,
  LayoutDashboard,
  LogIn,
  Ticket
} from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { authService } from "@/services/auth.service/auth.api";

export default function Index() {
  const [email, setEmail] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    setIsLoggedIn(authService.isAuthenticated());
  }, []);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Por favor, informe seu e-mail");
      return;
    }
    toast.success("Obrigado! Entraremos em contato em breve.");
    setEmail("");
  };

  const handleDemoRequest = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Solicitação enviada! Nossa equipe entrará em contato em breve.");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="border-b border-border sticky top-0 z-50 bg-background/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Logo size={30} textClassName="text-2xl font-extrabold" />

          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-sm font-medium hover:text-primary transition-colors">Funcionalidades</a>
            <a href="#pricing" className="text-sm font-medium hover:text-primary transition-colors">Planos</a>
            <a href="#testimonials" className="text-sm font-medium hover:text-primary transition-colors">Depoimentos</a>
            <a href="#cta" className="text-sm font-medium hover:text-primary transition-colors">Contato</a>
          </div>

          <div className="flex items-center space-x-3">
            <ThemeToggle />
            {isLoggedIn ? (
              <Link to="/dashboard">
                <Button size="sm" className="gap-1.5">
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button size="sm" variant="outline" className="gap-1.5">
                    <LogIn className="h-4 w-4" />
                    Entrar
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm">Cadastre-se</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 lg:py-24 hero-section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6 opacity-0 animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <Zap className="h-3.5 w-3.5" />
                Atendimento inteligente com IA
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight">
                Atendimento <span className="text-gradient">Omnichannel</span> sem complicação
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Gerencie todos os seus atendimentos em um só lugar. Site, Slack e canais integrados em uma única plataforma.
              </p>
              <p className="text-base font-medium text-primary">
                Teste grátis por 30 dias. Sem cartão de crédito.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button size="lg" className="rounded-full font-semibold" onClick={() => window.location.href = '/register'}>
                  Começar 30 dias grátis
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-full">
                  Ver Demonstração
                </Button>
              </div>

              <div className="pt-4 flex items-center space-x-4">
                <Button variant="link" className="p-0 h-auto text-sm flex items-center gap-1">
                  <Download className="h-3 w-3" />
                  Download PDF
                </Button>
                <Button variant="link" className="p-0 h-auto text-sm flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Agendar Demo
                </Button>
              </div>
            </div>

            <div className="flex justify-center opacity-0 animate-fadeIn animate-delay-200">
              <div className="glass-card p-4 w-full max-w-md relative overflow-hidden">
                <div className="absolute -right-20 -top-20 w-40 h-40 bg-primary/10 rounded-full blur-3xl"></div>
                <div className="absolute -left-20 -bottom-20 w-40 h-40 bg-brand-purple/10 rounded-full blur-3xl"></div>

                <div className="bg-background rounded-lg shadow-sm border border-border overflow-hidden">
                  <div className="bg-primary/5 p-3 border-b border-border flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="text-xs font-medium flex items-center gap-1">
                      <Ticket className="h-3 w-3 text-primary" />
                      BaitaTicket — Protocolo BT-483729
                    </div>
                    <div className="w-4"></div>
                  </div>

                  <div className="p-4 space-y-3">
                    <div className="ticket-bubble-client">
                      <p className="text-sm">Olá, preciso de ajuda com meu pedido.</p>
                      <div className="text-xs text-muted-foreground mt-1">10:42</div>
                    </div>

                    <div className="ticket-bubble-agent">
                      <p className="text-sm">Olá! Claro, fique feliz em ajudar. Seu protocolo é <strong>BT-483729</strong>.</p>
                      <div className="text-xs text-muted-foreground mt-1 text-right">10:43</div>
                    </div>

                    <div className="ticket-bubble-client">
                      <p className="text-sm">Perfeito, anotei o protocolo. Como acompanho?</p>
                      <div className="text-xs text-muted-foreground mt-1">10:44</div>
                    </div>

                    <div className="ticket-bubble-agent animate-pulse">
                      <p className="text-sm">Estou verificando seu pedido...</p>
                      <div className="text-xs text-muted-foreground mt-1 text-right">10:45</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20">
            <div className="text-center opacity-0 animate-fadeIn animate-delay-100">
              <div className="text-4xl font-extrabold text-gradient">+5000</div>
              <p className="text-muted-foreground mt-2">Empresas atendidas</p>
            </div>
            <div className="text-center opacity-0 animate-fadeIn animate-delay-200">
              <div className="text-4xl font-extrabold text-gradient">+1M</div>
              <p className="text-muted-foreground mt-2">Conversas mensais</p>
            </div>
            <div className="text-center opacity-0 animate-fadeIn animate-delay-300">
              <div className="text-4xl font-extrabold text-gradient">97%</div>
              <p className="text-muted-foreground mt-2">De satisfação</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 lg:py-24 bg-secondary/50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold">Funcionalidades Principais</h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Tudo o que você precisa para oferecer uma experiência omnichannel excepcional
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="neo-card p-6 hover-scale opacity-0 animate-fadeIn animate-delay-100">
              <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <MessageCircle className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-2">Canais Integrados</h3>
              <p className="text-muted-foreground">
                Conecte Slack, web e outros canais e gerencie todas as conversas diretamente na plataforma.
              </p>
              <Button variant="link" className="mt-4 p-0 flex items-center">
                Saiba mais <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>

            <div className="neo-card p-6 hover-scale opacity-0 animate-fadeIn animate-delay-200">
              <div className="h-12 w-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Gestão de Equipes</h3>
              <p className="text-muted-foreground">
                Crie grupos internos, defina permissões e monitore o desempenho de cada atendente.
              </p>
              <Button variant="link" className="mt-4 p-0 flex items-center">
                Saiba mais <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>

            <div className="neo-card p-6 hover-scale opacity-0 animate-fadeIn animate-delay-300">
              <div className="h-12 w-12 bg-green-500/10 rounded-xl flex items-center justify-center mb-4">
                <BarChart className="h-6 w-6 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Relatórios Detalhados</h3>
              <p className="text-muted-foreground">
                Acompanhe métricas importantes como tempo de resposta, tickets resolvidos e satisfação.
              </p>
              <Button variant="link" className="mt-4 p-0 flex items-center">
                Saiba mais <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>

            <div className="neo-card p-6 hover-scale opacity-0 animate-fadeIn animate-delay-400">
              <div className="h-12 w-12 bg-orange-500/10 rounded-xl flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Automações</h3>
              <p className="text-muted-foreground">
                Configure respostas automáticas e fluxos de atendimento para otimizar o processo.
              </p>
              <Button variant="link" className="mt-4 p-0 flex items-center">
                Saiba mais <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Button className="gap-2">
              Ver todas as funcionalidades
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Demo Request Banner */}
      <section className="py-12 bg-gradient-to-r from-brand-purple/20 to-brand-blue/20">
        <div className="container mx-auto px-4">
          <div className="rounded-2xl bg-background border border-border p-8 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-extrabold mb-4">Veja o BaitaTicket em ação</h2>
                <p className="text-muted-foreground mb-4">
                  Solicite uma demonstração personalizada e descubra como podemos transformar o atendimento da sua empresa.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span>Demo guiada por um especialista</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span>Conheça casos de sucesso similares ao seu</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span>Tire todas as suas dúvidas em tempo real</span>
                  </li>
                </ul>
              </div>

              <div>
                <form onSubmit={handleDemoRequest} className="neo-card p-6">
                  <h3 className="font-semibold mb-4">Agende uma demonstração</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Input placeholder="Nome" required />
                      </div>
                      <div>
                        <Input placeholder="Sobrenome" required />
                      </div>
                    </div>
                    <Input type="email" placeholder="Email corporativo" required />
                    <Input placeholder="Empresa" required />
                    <Input placeholder="Telefone" required />
                    <Button type="submit" className="w-full">Solicitar demonstração</Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold">O que nossos clientes dizem</h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Centenas de empresas confiam no BaitaTicket para seu atendimento ao cliente
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="neo-card p-6 hover-scale">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  {[...Array(5)].map((_, i) => <Star key={i} className="fill-current h-4 w-4" />)}
                </div>
              </div>
              <p className="mb-4">
                "O BaitaTicket transformou completamente nosso atendimento. Conseguimos reduzir o tempo de resposta em 70% e melhorar a satisfação dos clientes."
              </p>
              <div className="flex items-center">
                <div className="rounded-full bg-primary/20 w-10 h-10 flex items-center justify-center text-primary font-semibold">RM</div>
                <div className="ml-3">
                  <p className="font-medium">Roberto Mendes</p>
                  <p className="text-sm text-muted-foreground">CEO, TechSolutions</p>
                </div>
              </div>
            </div>

            <div className="neo-card p-6 hover-scale">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  {[...Array(5)].map((_, i) => <Star key={i} className="fill-current h-4 w-4" />)}
                </div>
              </div>
              <p className="mb-4">
                "A capacidade de integrar todos os canais em um só lugar nos deu uma visão completa do cliente. Isso é impagável!"
              </p>
              <div className="flex items-center">
                <div className="rounded-full bg-primary/20 w-10 h-10 flex items-center justify-center text-primary font-semibold">CA</div>
                <div className="ml-3">
                  <p className="font-medium">Carla Almeida</p>
                  <p className="text-sm text-muted-foreground">Diretora de Marketing, Shopize</p>
                </div>
              </div>
            </div>

            <div className="neo-card p-6 hover-scale">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  {[...Array(5)].map((_, i) => <Star key={i} className="fill-current h-4 w-4" />)}
                </div>
              </div>
              <p className="mb-4">
                "Conseguimos escalar nosso suporte sem aumentar a equipe, graças às automações do BaitaTicket. Excelente retorno sobre o investimento."
              </p>
              <div className="flex items-center">
                <div className="rounded-full bg-primary/20 w-10 h-10 flex items-center justify-center text-primary font-semibold">FT</div>
                <div className="ml-3">
                  <p className="font-medium">Felipe Torres</p>
                  <p className="text-sm text-muted-foreground">COO, GrowthPartners</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Button variant="outline" className="gap-2">
              Ver mais depoimentos
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 lg:py-24 bg-secondary/50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold">Plano único para o seu negócio</h2>
            <p className="mt-4 text-lg text-muted-foreground">
              R$ 2.000/mês. Os primeiros 30 dias são grátis, sem cartão de crédito. Depois do teste, você escolhe se quer continuar.
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <div className="neo-card p-6 relative border-primary hover-scale opacity-0 animate-fadeIn animate-delay-100">
              <div className="text-lg font-bold mb-2">BaitaTicket</div>
              <div className="text-3xl font-extrabold mb-1">
                R$ 2.000<span className="text-sm text-muted-foreground font-normal">/mês</span>
              </div>
              <p className="text-sm text-primary font-medium mb-6">
                Teste grátis por 30 dias · Sem cartão de crédito
              </p>
              <p className="text-muted-foreground mb-6">
                Tudo que você precisa para atendimento digital com IA.
              </p>

              <div className="space-y-3 mb-6">
                {["Canais de atendimento", "Atendentes simultâneos", "Tickets ilimitados", "Relatórios e automações"].map((item) => (
                  <div key={item} className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-primary mr-2 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <Button className="w-full font-semibold" onClick={() => window.location.href = '/register'}>
                Começar 30 dias grátis
              </Button>
              <div className="text-center mt-3 text-sm text-muted-foreground">
                Sem cartão. Cancele quando quiser.
              </div>
            </div>
          </div>

          <div className="max-w-3xl mx-auto mt-16 space-y-6" id="faq">
            <h3 className="text-2xl font-extrabold text-center">Perguntas frequentes</h3>
            <div>
              <h4 className="font-semibold">Preciso de cartão para testar?</h4>
              <p className="text-muted-foreground">
                Não. Você usa o BaitaTicket por 30 dias sem informar cartão de crédito.
              </p>
            </div>
            <div>
              <h4 className="font-semibold">O que acontece depois dos 30 dias?</h4>
              <p className="text-muted-foreground">
                Avisamos você antes do fim do teste. Para continuar, você ativa o plano de R$ 2.000/mês e informa a forma de pagamento nessa hora. Se não ativar, nada é cobrado.
              </p>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Teste gratuito de 30 dias, sem necessidade de cartão de crédito. Não há cobrança automática ao fim do período. A cobrança só começa quando o cliente ativar o plano.
            </p>
          </div>

          <CommercialContactCard className="mt-16 max-w-4xl mx-auto" />
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold mb-4">
              Receba novidades e dicas exclusivas
            </h2>
            <p className="text-muted-foreground mb-8">
              Inscreva-se para receber conteúdos sobre atendimento ao cliente, novidades da plataforma e dicas para melhorar seus resultados.
            </p>

            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Seu melhor email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
                required
              />
              <Button type="submit">Inscrever-se</Button>
            </form>

            <p className="text-xs text-muted-foreground mt-4">
              Você pode cancelar a inscrição a qualquer momento. Leia nossa Política de Privacidade.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="cta" className="py-16 lg:py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-6">
            Pronto para revolucionar seu atendimento?
          </h2>
          <p className="text-xl opacity-90 max-w-2xl mx-auto mb-8">
            Junte-se a milhares de empresas que já transformaram a experiência de atendimento com o BaitaTicket
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="rounded-full font-semibold" onClick={() => window.location.href = '/register'}>
              Iniciar Avaliação Gratuita
            </Button>
            <Button size="lg" variant="outline" className="rounded-full bg-transparent text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary">
              Agendar Demonstração
            </Button>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row justify-center gap-8">
            <div className="flex items-center gap-3">
              <PhoneCall className="h-5 w-5" />
              <span>+55 11 3456-7890</span>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5" />
              <span>contato@baitaticket.com.br</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="mb-4">
                <Logo size={26} textClassName="text-xl font-extrabold" />
              </div>
              <p className="text-muted-foreground">
                Plataforma omnichannel para gestão de atendimentos inteligente.
              </p>
              <div className="flex space-x-4 mt-4">
                {[
                  <path key="fb" d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
                  <path key="tw" d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />,
                ].map((p, i) => (
                  <a key={i} href="#" className="text-muted-foreground hover:text-foreground">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">{p}</svg>
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Produto</h4>
              <ul className="space-y-2">
                {["Funcionalidades", "Preços", "Integrações", "Novidades", "API"].map((item) => (
                  <li key={item}><a href="#" className="text-muted-foreground hover:text-foreground">{item}</a></li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Empresa</h4>
              <ul className="space-y-2">
                {["Sobre nós", "Contato", "Carreiras", "Blog", "Parceiros"].map((item) => (
                  <li key={item}><a href="#" className="text-muted-foreground hover:text-foreground">{item}</a></li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Suporte</h4>
              <ul className="space-y-2">
                {["Central de Ajuda", "Documentação", "Status da Plataforma", "Vídeos de Treinamento", "Webinars"].map((item) => (
                  <li key={item}><a href="#" className="text-muted-foreground hover:text-foreground">{item}</a></li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} BaitaTicket. Todos os direitos reservados.
            </p>

            <div className="flex space-x-6 mt-4 md:mt-0">
              {["Termos", "Privacidade", "Cookies", "Acessibilidade"].map((item) => (
                <a key={item} href="#" className="text-muted-foreground hover:text-foreground">{item}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
